const tryExtractMessageFromJsonString = (value) => {
    const text = typeof value === 'string' ? value.trim() : '';

    if (!text || (!text.startsWith('{') && !text.startsWith('['))) {
        return '';
    }

    try {
        const parsed = JSON.parse(text);

        if (parsed && typeof parsed === 'object') {
            return (
                String(parsed.message || parsed.error || parsed.detail || '')
                    .trim()
            );
        }
    } catch (_error) {
    }

    return '';
};

const HTML_RESPONSE_PATTERN = /<\s*!doctype\s+html|<\s*html[\s>]|<\s*(title|h1|body|p)\b/i;

const isHtmlResponseText = (value) =>
    typeof value === 'string' && HTML_RESPONSE_PATTERN.test(value);

const stripHtmlToText = (value) =>
    String(value || '')
        .replace(/<\s*br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const limitMessageLength = (value, maxLength = 280) => {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text || text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 1).trim()}...`;
};

const humanizeFieldName = (value) =>
    String(value || '')
        .replace(/\[[0-9]+\]/g, '')
        .replace(/[._-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const getMessageFromValidationErrors = (errors) => {
    if (!Array.isArray(errors) || errors.length === 0) {
        return '';
    }

    const messages = [];
    const seen = new Set();

    errors.forEach((item) => {
        const rawMessage = String(item?.msg || item?.message || '').trim();
        if (!rawMessage) return;

        const field = humanizeFieldName(item?.path || item?.param || item?.field);
        const message = field && !rawMessage.toLowerCase().includes(field.toLowerCase())
            ? `${field}: ${rawMessage}`
            : rawMessage;

        if (!seen.has(message)) {
            seen.add(message);
            messages.push(message);
        }
    });

    if (messages.length <= 2) {
        return messages.join(' ');
    }

    return `${messages.slice(0, 2).join(' ')} And ${messages.length - 2} more issue(s).`;
};

const getMessageFromResponseData = (data) => {
    if (!data) {
        return '';
    }

    if (typeof data === 'string') {
        const parsedMessage = tryExtractMessageFromJsonString(data);
        if (parsedMessage) return parsedMessage;
        if (isHtmlResponseText(data)) return '';
        return limitMessageLength(data);
    }

    if (Array.isArray(data)) {
        return getMessageFromValidationErrors(data);
    }

    if (typeof data === 'object') {
        const validationMessage = getMessageFromValidationErrors(data.errors);
        if (validationMessage) {
            return validationMessage;
        }

        const directMessage = String(
            data.message || data.error || data.detail || ''
        ).trim();

        if (directMessage) {
            return tryExtractMessageFromJsonString(directMessage) || directMessage;
        }
    }

    return '';
};

const getHttpStatusMessage = (status, fallbackMessage = '') => {
    const fallback = fallbackMessage || 'Please try again.';

    if (status === 400) return `The request is invalid. ${fallback}`;
    if (status === 401) return 'Your session has expired. Please sign in again.';
    if (status === 403) return 'You do not have permission to perform this action.';
    if (status === 404) return 'The requested service endpoint was not found. Please refresh the page and try again.';
    if (status === 409) return `This action conflicts with existing data. ${fallback}`;
    if (status === 413) return 'The uploaded file is too large.';
    if (status === 415) return 'This file type is not supported.';
    if (status >= 500) return 'The server could not complete the request. Please try again later.';

    return fallback;
};

export const getErrorMessage = (err, fallbackMessage = '') => {
    if (err.response) {
        const responseMessage = getMessageFromResponseData(err.response.data);
        return (
            responseMessage ||
            getHttpStatusMessage(err.response.status, fallbackMessage)
        );
    } else if (err.request) {
        return 'Could not connect to the server. Please check your network connection.';
    } else {
        if (isHtmlResponseText(err.message)) {
            return limitMessageLength(stripHtmlToText(err.message)) || fallbackMessage || 'Something went wrong. Please try again.';
        }

        return (
            tryExtractMessageFromJsonString(err.message) ||
            limitMessageLength(err.message) ||
            fallbackMessage ||
            'Something went wrong. Please try again.'
        );
    }
};
